import { GameRenderer, GameResult } from '../../interfaces/game-type-plugin.interface';
import {
  GameBoardSettings,
  GameBoardState,
  BoardSpace,
  RollOutcome,
  DEFAULT_PRIZE_COLORS,
  BOARD_SIZE_PIXELS,
  PIXELS_PER_UNIT,
  CORNER_UNITS,
  SPACE_WIDTH_UNITS,
  SPACE_HEIGHT_UNITS,
  SPACES_PER_SIDE
} from './game-board.types';
import { GameBoardSceneComponent } from './game-board-scene.component';

import { MockPrizeApiService } from '@kanek/shared-data-access';

// Constants
const WIN_CELEBRATION_DELAY_MS = 2000;

// Sound asset paths
const ASSET_PATHS = {
  WIN_SOUND: 'assets/sfx/scratchcard-win.mp3', // Reuse scratchcard win sound
  DRAMA_SOUND: 'assets/sfx/scratchcard-two-match.mp3', // Reuse scratchcard drama sound for 3rd ROLL AGAIN
} as const;

/**
 * Configuration object for GameBoardRenderer.updateConfig()
 */
export interface GameBoardRendererConfig {
  settings: GameBoardSettings;
  items: Array<{
    id?: string;
    label?: string;
    value?: string;
    icon?: string;
    probability?: number;
    [key: string]: unknown
  }>;
}

/**
 * Game Board renderer
 *
 * Handles board generation, dice rolling, game piece movement, and Prize API integration.
 *
 * ARCHITECTURE:
 * - HTML/CSS: Board layout with prize spaces
 * - 3D: Die (Cannon.js physics), Pawn (GLB model), Board base (box geometry)
 * - Game Logic: Prize distribution, roll outcome calculation, extended play
 */
export class GameBoardRenderer implements GameRenderer<GameBoardRendererConfig> {
  private container: HTMLElement | null = null;
  private config: GameBoardRendererConfig | null = null;
  private boardSpaces: BoardSpace[] = [];
  private gameState: GameBoardState | null = null;

  // Canvas store for accessing scene.userData (sceneGraph mode)
  private canvasStore: any | null = null;

  // ArService for keepVisible flag during critical moments
  private arService?: any;

  // Optional callbacks
  public onRoll?: () => void;
  public onLand?: (position: number) => void;
  public onWin?: (prizeId: string) => void;

  setCanvasStore(store: any): void {
    this.canvasStore = store;
  }

  setArService(arService: any): void {
    this.arService = arService;
  }

  /**
   * Initialize renderer with HTML container
   */
  initialize(container: HTMLElement, arService?: any): void {
    this.container = container;
    this.arService = arService;

    // Set container styles
    container.style.position = 'relative';
    container.style.width = '100%';
    container.style.height = '100%';
  }

  /**
   * Update configuration and regenerate board
   */
  async updateConfig(config: GameBoardRendererConfig): Promise<void> {
    const previousConfig = this.config;
    this.config = config;

    // Only reset game state when:
    // 1. Explicit reset requested (_resetRequested flag)
    // 2. Prize list changed (items array)
    // 3. Extended play probability changed (affects gameplay)
    const resetRequested = (config.settings as any)._resetRequested;
    const prizesChanged = !previousConfig ||
      JSON.stringify(previousConfig.items) !== JSON.stringify(config.items);
    const extendedPlayChanged = !previousConfig ||
      previousConfig.settings.extendedPlayProbability !== config.settings.extendedPlayProbability;

    if (resetRequested || prizesChanged || extendedPlayChanged) {
      this.gameState = null;

      // Clear dramatic glow effects and release the keepVisible lock on explicit reset
      if (resetRequested) {
        this.removeCriticalPrizeGlow();
      }

      // Trigger full visual reset (pawn position, dice, button) whenever game state is cleared
      if (this.canvasStore) {
        const scene = this.canvasStore.scene;
        const resetGameFn = scene?.userData?.['resetGame'];
        if (resetGameFn && typeof resetGameFn === 'function') {
          resetGameFn();
        }
      }
    }

    // Validate minimum prizes
    if (config.items.length < 8) {
      throw new Error('Game Board requires at least 8 prizes');
    }

    // Generate board layout
    this.boardSpaces = this.generateBoardSpaces(config);

    // Render board HTML
    if (this.container) {
      this.renderBoard(this.container);
    }

    // Update scene.userData if in sceneGraph mode
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      if (scene) {
        scene.userData = scene.userData || {};
        scene.userData['config'] = config;
        scene.userData['boardSpaces'] = this.boardSpaces;
      }
    }
  }

  /**
   * Play the game (called by game-engine on each CTA button click)
   *
   * For game-board, this performs a SINGLE ROLL rather than the entire game.
   * The button will be clicked multiple times until the game completes.
   */
  async play(options?: { getPrizeIndex?: () => Promise<number> }): Promise<GameResult | null> {
    if (!this.config) {
      throw new Error('Game Board not configured');
    }

    // First call OR after reset: Initialize game state
    if (!this.gameState) {
      // Determine winning prize
      let winningIndex: number;

      if (options?.getPrizeIndex) {
        // Production mode - call platform Prize API
        winningIndex = await options.getPrizeIndex();
      } else if ((this.config.settings as any)._previewPrizes) {
        // Preview mode - random selection
        winningIndex = Math.floor(Math.random() * this.config.items.length);
      } else {
        throw new Error('No Prize API callback and not in preview mode');
      }

      const winningPrize = this.config.items[winningIndex];

      // Initialize game state (determines winning position and extended play)
      this.gameState = this.initializeGameState(winningPrize, winningIndex);

      // Enable keepVisible for entire game session
      if (this.arService) {
        this.arService.keepVisible = true;
      }
    }

    // Perform single roll
    await this.handleSingleRoll();

    // Check if game complete
    if (this.gameState.hasWon) {
      const winningPrize = this.config.items[this.gameState.winningPrizeIndex];

      // Note: keepVisible will be disabled after celebration in handleWin() setTimeout

      // Return result (game complete)
      return {
        isWinner: true,
        prize: {
          id: winningPrize.id || '',
          name: winningPrize.label || '',
          type: (winningPrize['type'] as string) || 'prize'
        },
        metadata: {
          prizeIndex: this.gameState.winningPrizeIndex,
          rollCount: this.gameState.rollCount,
          isExtendedPlay: this.gameState.isExtendedPlay
        }
      };
    }

    // Return null (game not complete, need more rolls)
    return null;
  }



  /**
   * Cleanup resources
   */
  destroy(): void {
    // Remove dramatic glow effects
    this.removeCriticalPrizeGlow();

    // Remove injected styles
    const styleElement = document.getElementById('game-board-styles');
    if (styleElement) {
      styleElement.remove();
    }

    if (this.container) {
      this.container.innerHTML = '';
    }
    this.config = null;
    this.boardSpaces = [];
    this.gameState = null;
  }

  /**
   * Reset the game board to its initial state: pawn returns to START, all revealed
   * cells are hidden, and the next `play()` call starts a fresh game.
   *
   * Uses the existing `_resetRequested` flag so `updateConfig()` handles both the
   * 3D scene reset (pawn position, dice) and the HTML re-render in one pass.
   *
   * Preview-only: called from GameEngineComponent.resetGame() guarded by previewMode.
   */
  async reset(): Promise<void> {
    if (!this.config) return;

    await this.updateConfig({
      ...this.config,
      settings: { ...this.config.settings, _resetRequested: true } as GameBoardSettings,
    });
  }

  /**
   * Get scene component for sceneGraph mode
   */
  getSceneComponent() {
    return GameBoardSceneComponent;
  }

  // ==================== PRIVATE METHODS ====================

  /**
   * Generate board spaces from prize list
   */
  private generateBoardSpaces(config: GameBoardRendererConfig): BoardSpace[] {
    const spaces: BoardSpace[] = [];
    const prizes = [...config.items].sort((a, b) =>
      (b.probability || 0) - (a.probability || 0) // Sort by probability DESC (lowest value first)
    );

    const colors = config.settings.prizeColors || DEFAULT_PRIZE_COLORS;

    // Position 0: START corner
    spaces.push({
      type: 'start',
      position: 0,
      side: 1,
      color: '#4CAF50'
    });

    // Side 1 (positions 1-5): 3× Prize#0 + 2× Prize#1
    for (let i = 1; i <= 5; i++) {
      const prizeIndex = i <= 3 ? 0 : 1;
      const prize = prizes[prizeIndex];
      spaces.push({
        type: 'prize',
        position: i,
        side: 1,
        prizeId: prize.id,
        prizeIndex: prizeIndex,
        prizeName: prize.label,
        prizeValue: prize.value,
        prizeIcon: prize.icon,
        color: colors[prizeIndex % colors.length]
      });
    }

    // Position 6: ROLL AGAIN corner
    spaces.push({
      type: 'roll-again',
      position: 6,
      side: 2,
      color: '#FFC107'
    });

    // Side 2 (positions 7-11): 3× Prize#2 + 2× Prize#3
    for (let i = 7; i <= 11; i++) {
      const prizeIndex = i <= 9 ? 2 : 3;
      const prize = prizes[prizeIndex];
      spaces.push({
        type: 'prize',
        position: i,
        side: 2,
        prizeId: prize.id,
        prizeIndex: prizeIndex,
        prizeName: prize.label,
        prizeValue: prize.value,
        prizeIcon: prize.icon,
        color: colors[prizeIndex % colors.length]
      });
    }

    // Position 12: ROLL AGAIN corner
    spaces.push({
      type: 'roll-again',
      position: 12,
      side: 3,
      color: '#FFC107'
    });

    // Side 3 (positions 13-17): 3× Prize#4 + 2× Prize#5
    for (let i = 13; i <= 17; i++) {
      const prizeIndex = i <= 15 ? 4 : 5;
      const prize = prizes[prizeIndex];
      spaces.push({
        type: 'prize',
        position: i,
        side: 3,
        prizeId: prize.id,
        prizeIndex: prizeIndex,
        prizeName: prize.label,
        prizeValue: prize.value,
        prizeIcon: prize.icon,
        color: colors[prizeIndex % colors.length]
      });
    }

    // Position 18: ROLL AGAIN corner
    spaces.push({
      type: 'roll-again',
      position: 18,
      side: 4,
      color: '#FFC107'
    });

    // Side 4 (positions 19-23): Prize#6 + Prizes#0-3
    // Position 19: Prize#6 (2nd highest value)
    spaces.push({
      type: 'prize',
      position: 19,
      side: 4,
      prizeId: prizes[6].id,
      prizeIndex: 6,
      prizeName: prizes[6].label,
      prizeValue: prizes[6].value,
      prizeIcon: prizes[6].icon,
      color: colors[6 % colors.length]
    });

    // Positions 20-23: Bottom 4 prizes (Prize#0-3)
    for (let i = 20; i <= 23; i++) {
      const prizeIndex = i - 20;
      const prize = prizes[prizeIndex];
      spaces.push({
        type: 'prize',
        position: i,
        side: 4,
        prizeId: prize.id,
        prizeIndex: prizeIndex,
        prizeName: prize.label,
        prizeValue: prize.value,
        prizeIcon: prize.icon,
        color: colors[prizeIndex % colors.length]
      });
    }

    // Add grand prize overlay on START position (will be rendered differently)
    spaces[0].type = 'grand-prize';
    spaces[0].prizeId = prizes[7].id;
    spaces[0].prizeIndex = 7;
    spaces[0].prizeName = prizes[7].label;
    spaces[0].prizeValue = prizes[7].value;
    spaces[0].prizeIcon = prizes[7].icon;
    spaces[0].color = colors[7 % colors.length];

    return spaces;
  }

  /**
   * Render board HTML
   */
  private renderBoard(container: HTMLElement): void {
    if (!this.config) return;

    // Get board size from settings
    const boardSize = this.config.settings.boardSize || 'medium';
    const boardPixels = BOARD_SIZE_PIXELS[boardSize];

    container.innerHTML = `
      <div class="game-board">
        <div class="board-spaces">
          ${this.boardSpaces.map(space => this.renderSpace(space)).join('')}
        </div>
        <div class="board-center">
          ${this.config.settings.logoUrl
            ? `<img src="${this.config.settings.logoUrl}" alt="Logo" />`
            : `<h2>${this.config.settings.centerText || 'GAME BOARD'}</h2>`
          }
        </div>
      </div>
    `;

    // Add styles
    this.injectStyles();

    // Note: ROLL button will be added as separate UI overlay by game-engine
    // Similar to how wheel has SPIN button as overlay
  }

  /**
   * Render individual space HTML
   */
  private renderSpace(space: BoardSpace): string {
    const positionClass = `space-${space.position}`;
    const typeClass = `space-${space.type}`;
    const sideClass = `side-${space.side}`;

    if (space.type === 'start' || space.type === 'grand-prize') {
      return `
        <div class="${positionClass} ${typeClass} ${sideClass} board-space corner-space" data-position="${space.position}">
          <div class="start-section">
            <div class="corner-label">START</div>
          </div>
          ${space.type === 'grand-prize' ? `
            <div class="grand-prize-section" style="background-color: ${space.color}">
              <div class="prize-name">${space.prizeName}</div>
              <div class="prize-value">${space.prizeValue}</div>
            </div>
          ` : ''}
        </div>
      `;
    }

    if (space.type === 'roll-again') {
      return `
        <div class="${positionClass} ${typeClass} ${sideClass} board-space corner-space" data-position="${space.position}">
          <div class="corner-label">ROLL<br>AGAIN</div>
        </div>
      `;
    }

    // Determine space orientation and color bar position based on side
    const isHorizontal = space.side === 1 || space.side === 3; // Bottom or Top
    const spaceClass = isHorizontal ? 'prize-space-horizontal' : 'prize-space-vertical';

    // Color bar position: bottom=top bar, left=RIGHT bar (inside), top=bottom bar, right=LEFT bar (inside)
    let colorBarClass = '';
    let colorBarPosition = '';
    if (space.side === 1) { // Bottom side
      colorBarClass = 'color-bar-top';
    } else if (space.side === 2) { // Left side
      colorBarClass = 'color-bar-right'; // Inside edge
    } else if (space.side === 3) { // Top side
      colorBarClass = 'color-bar-bottom';
      colorBarPosition = 'order: 2;'; // Move to bottom
    } else if (space.side === 4) { // Right side
      colorBarClass = 'color-bar-left'; // Inside edge
    }

    // Text rotation: no rotation for bottom, 90° for left, 180° for top, -90° for right
    let textRotation = 0;
    if (space.side === 2) textRotation = 90;
    else if (space.side === 3) textRotation = 180;
    else if (space.side === 4) textRotation = -90;

    return `
      <div class="${positionClass} ${typeClass} ${sideClass} board-space ${spaceClass}" data-position="${space.position}">
        <div class="${colorBarClass}" style="background-color: ${space.color}; ${colorBarPosition}"></div>
        <div class="prize-content">
          <div class="prize-text-container" style="transform: rotate(${textRotation}deg);">
            ${space.prizeIcon ? `<img src="${space.prizeIcon}" class="prize-icon" />` : ''}
            <div class="prize-name">${space.prizeName}</div>
            <div class="prize-value">${space.prizeValue}</div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Inject CSS styles (Unit-Based Sizing - No Fractions!)
   */
  private injectStyles(): void {
    const styleId = 'game-board-styles';

    // Remove existing styles to allow regeneration when board size changes
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Get board size preset
    const boardSize = this.config?.settings.boardSize || 'medium';

    // Calculate dimensions from logical units (NO SCALING - Direct multiplication!)
    const pixelsPerUnit = PIXELS_PER_UNIT[boardSize];
    const boardPixels = BOARD_SIZE_PIXELS[boardSize];
    const cornerSize = CORNER_UNITS * pixelsPerUnit;        // e.g., 4 × 20 = 80px
    const spaceWidth = SPACE_WIDTH_UNITS * pixelsPerUnit;   // e.g., 3 × 20 = 60px
    const spaceHeight = SPACE_HEIGHT_UNITS * pixelsPerUnit; // e.g., 4 × 20 = 80px

    // Font and border sizes scaled proportionally
    const fontSize = Math.floor(pixelsPerUnit * 0.7);      // e.g., 20 × 0.7 = 14px
    const borderWidth = Math.max(2, Math.floor(pixelsPerUnit * 0.2)); // e.g., 20 × 0.2 = 4px

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .game-board {
        position: relative;
        width: ${boardPixels}px;
        height: ${boardPixels}px;
        background: #e8f5e9;
        user-select: none;
      }

      .board-spaces {
        position: absolute;
        inset: 0;
      }

      .board-space {
        position: absolute;
        background: white;
        border: 1px solid #333;
      }

      .corner-space {
        width: ${cornerSize}px;
        height: ${cornerSize}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: ${fontSize}px;
        text-align: center;
        overflow: hidden;
      }

      .corner-label {
        transform: rotate(45deg);
        line-height: 1.2;
        color: #444;  /* Dark text for better contrast */
        font-weight: bold;
        z-index: 2;
      }

      /* START corner diagonal split */
      .space-grand-prize .start-section {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding-left: ${Math.floor(pixelsPerUnit * 0.3)}px;
        background: white;
        clip-path: polygon(0% 0%, 0% 100%, 100% 100%);
        z-index: 1;
      }

      /* START text vertical on left side (reading bottom to top) */
      .space-grand-prize .start-section .corner-label {
        transform: rotate(-135deg) translateY(-50%);
        width: 100%;
        white-space: nowrap;
      }

      .space-grand-prize .grand-prize-section {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: center;
        padding-right: ${Math.floor(pixelsPerUnit * 0.3)}px;
        clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
        z-index: 0;
      }

      .space-grand-prize .grand-prize-section .prize-name {
        font-size: ${Math.floor(pixelsPerUnit * 0.6)}px;
        transform: rotate(45deg) translateY(-50%);
        width: 100%;
        white-space: nowrap;
      }

      .space-grand-prize .grand-prize-section .prize-value {
        font-size: ${Math.floor(pixelsPerUnit * 0.8)}px;
        transform: rotate(45deg) translateY(-50%);
        white-space: nowrap;
        display: none;
      }

      /* ROLL AGAIN corner (side 2, position 6) - default 45deg */
      .side-2.corner-space .corner-label {
        transform: rotate(45deg);
      }

      /* Top-left ROLL AGAIN corner (side 3, position 12) - rotate towards center */
      .side-3.corner-space .corner-label {
        transform: rotate(135deg);
      }

      /* Top-right ROLL AGAIN corner (side 4, position 18) - rotate towards center */
      .side-4.corner-space .corner-label {
        transform: rotate(-135deg);
      }

      /* Horizontal spaces (top/bottom sides): 3 units wide × 4 units tall */
      .prize-space-horizontal {
        width: ${spaceWidth}px;
        height: ${spaceHeight}px;
        display: flex;
        flex-direction: column;
      }

      /* Vertical spaces (left/right sides): 4 units wide × 3 units tall */
      .prize-space-vertical {
        width: ${spaceHeight}px;  /* Swapped dimensions */
        height: ${spaceWidth}px;
        display: flex;
        flex-direction: column;
      }

      /* Color bar positioning by side */
      .color-bar-top,
      .color-bar-bottom {
        height: ${Math.floor(pixelsPerUnit * 0.5)}px;  /* 0.5 units */
        width: 100%;
      }

      /* Inner border on bottom edge (facing prize content) */
      .color-bar-top {
        border-bottom: 1px solid rgba(0, 0, 0, 0.2);
      }

      .color-bar-bottom {
        border-top: 1px solid rgba(0, 0, 0, 0.2);
      }

      .color-bar-left,
      .color-bar-right {
        width: ${Math.floor(pixelsPerUnit * 0.5)}px;  /* 0.5 units */
        height: 100%;
        position: absolute;
        top: 0;
      }

      .color-bar-left {
        left: 0;
        border-right: 1px solid rgba(0, 0, 0, 0.2);
      }

      .color-bar-right {
        right: 0;
        border-left: 1px solid rgba(0, 0, 0, 0.2);
      }

      /* Prize content container - SQUARE for rotation */
      .prize-content {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      }

      /* Rotatable square content inside prize-content */
      .prize-text-container {
        width: ${Math.min(spaceWidth, spaceHeight) - Math.floor(pixelsPerUnit * 0.4)}px;  /* Square, with padding */
        height: ${Math.min(spaceWidth, spaceHeight) - Math.floor(pixelsPerUnit * 0.4)}px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-size: ${Math.floor(pixelsPerUnit * 0.5)}px;
      }

      .prize-icon {
        width: ${Math.floor(pixelsPerUnit * 1.2)}px;
        height: ${Math.floor(pixelsPerUnit * 1.2)}px;
        margin-bottom: ${Math.floor(pixelsPerUnit * 0.1)}px;
      }

      .prize-name {
        font-weight: bold;
        margin-bottom: ${Math.floor(pixelsPerUnit * 0.1)}px;
        color: #444;  /* Dark text for better contrast */
      }

      .prize-value {
        color: #555;  /* Dark text for better contrast */
      }

      .board-center {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${Math.floor(pixelsPerUnit * 15)}px;  /* Center area scales with board */
        height: ${Math.floor(pixelsPerUnit * 15)}px;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        border: 1px solid #333;
      }

      .board-center img {
        max-width: 80%;
        max-height: 80%;
      }

      .board-center h2 {
        font-size: ${Math.floor(pixelsPerUnit * 2.4)}px;  /* Scales with board size */
        font-weight: bold;
        color: #333;
        text-align: center;
        transform: rotate(-45deg);
      }

      /* Winning space glow effect - more vibrant than pre-win dramatic glow */
      .winning-space {
        animation: winningPulse 1s ease-in-out infinite;
        z-index: 15 !important;
      }

      @keyframes winningPulse {
        0%, 100% {
          box-shadow: 0 0 15px 8px rgba(76, 175, 80, 0.8),
                      0 0 30px 12px rgba(76, 175, 80, 0.6),
                      0 0 45px 16px rgba(76, 175, 80, 0.4),
                      inset 0 0 20px rgba(76, 175, 80, 0.5);
          border-color: #4CAF50 !important;
          border-width: ${Math.max(4, Math.floor(borderWidth * 2))}px !important;
          transform: scale(1);
        }
        50% {
          box-shadow: 0 0 25px 12px rgba(76, 175, 80, 1),
                      0 0 50px 20px rgba(76, 175, 80, 0.8),
                      0 0 75px 28px rgba(76, 175, 80, 0.6),
                      inset 0 0 30px rgba(76, 175, 80, 0.7);
          border-color: #66BB6A !important;
          border-width: ${Math.max(5, Math.floor(borderWidth * 2.5))}px !important;
          transform: scale(1.1);
        }
      }

      /* Dramatic glow animations for critical prize spaces */
      @keyframes dramatic-glow {
        0%, 100% {
          box-shadow: 0 0 10px rgba(255, 215, 0, 0.4),
                      0 0 20px rgba(255, 215, 0, 0.3),
                      0 0 30px rgba(255, 215, 0, 0.2),
                      inset 0 0 15px rgba(255, 215, 0, 0.2);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.8),
                      0 0 40px rgba(255, 215, 0, 0.6),
                      0 0 60px rgba(255, 215, 0, 0.4),
                      inset 0 0 25px rgba(255, 215, 0, 0.4);
          transform: scale(1.05);
        }
      }

      /* Directional glow animation for grand prize section (top and right edges) */
      @keyframes dramatic-glow-directional {
        0%, 100% {
          box-shadow: 0 -10px 20px rgba(255, 215, 0, 0.5),
                      0 -20px 40px rgba(255, 215, 0, 0.3),
                      0 -30px 60px rgba(255, 215, 0, 0.2),
                      10px 0 20px rgba(255, 215, 0, 0.5),
                      20px 0 40px rgba(255, 215, 0, 0.3),
                      30px 0 60px rgba(255, 215, 0, 0.2),
                      15px -15px 30px rgba(255, 215, 0, 0.6),
                      inset 0 0 20px rgba(255, 215, 0, 0.2);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 -10px 20px rgba(255, 215, 0, 0.8),
                      0 -20px 40px rgba(255, 215, 0, 0.6),
                      0 -30px 60px rgba(255, 215, 0, 0.4),
                      10px 0 20px rgba(255, 215, 0, 0.8),
                      20px 0 40px rgba(255, 215, 0, 0.6),
                      30px 0 60px rgba(255, 215, 0, 0.4),
                      15px -15px 30px rgba(255, 215, 0, 0.9),
                      inset 0 0 30px rgba(255, 215, 0, 0.4);
          transform: scale(1.05);
        }
      }

      @keyframes dramatic-pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.7;
        }
      }

      /* Dramatic glow class for critical prize spaces */
      .dramatic-glow {
        animation: dramatic-glow 2s ease-in-out infinite,
                   dramatic-pulse 2s ease-in-out infinite;
        z-index: 10 !important;
        border-color: #FFD700 !important;
        border-width: ${Math.max(4, Math.floor(borderWidth * 1.5))}px !important;
      }

      /* Enhanced glow for grand prize parent square (directional - top and right edges only) */
      .space-grand-prize.dramatic-glow {
        /* Override base animation with directional one */
        animation: dramatic-glow-directional 2s ease-in-out infinite !important;
        z-index: 10 !important;
        /* Only style top and right borders, leave bottom and left with default */
        border-top: ${Math.max(3, Math.floor(borderWidth * 1.5))}px solid #FFD700 !important;
        border-right: ${Math.max(3, Math.floor(borderWidth * 1.5))}px solid #FFD700 !important;
        border-bottom: 1px solid #333 !important;
        border-left: 1px solid #333 !important;
      }

      /* Pulse animation only for grand prize section when parent has dramatic-glow */
      .space-grand-prize.dramatic-glow .grand-prize-section {
        animation: dramatic-pulse 2s ease-in-out infinite;
      }

      /* Enhanced glow for second-top prize */
      .dramatic-glow.space-prize {
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.7),
                    0 0 40px rgba(255, 215, 0, 0.5),
                    0 0 60px rgba(255, 215, 0, 0.3),
                    inset 0 0 20px rgba(255, 215, 0, 0.3) !important;
      }

      /* Space positioning - will be set dynamically */
      ${this.generateSpacePositions()}
    `;

    document.head.appendChild(style);
  }

  /**
   * Highlight critical prize spaces with dramatic glow
   * Called when player lands on position 18 (3rd ROLL AGAIN) after rolling 3 sixes
   */
  private highlightCriticalPrizes(): void {
    // Try to find the board in DOM if container not set (sceneGraph mode)
    const boardSpaces = this.container?.querySelector('.board-spaces') || document.querySelector('.board-spaces');
    if (!boardSpaces) return;

    // Remove any existing glows first
    this.removeCriticalPrizeGlow();

    // Highlight position 19 (second-top prize - requires roll of 1)
    const space19 = boardSpaces.querySelector('[data-position="19"]') as HTMLElement;
    if (space19) {
      space19.classList.add('dramatic-glow');
    }

    // Highlight position 0 (grand prize - requires roll of 6)
    const space0 = boardSpaces.querySelector('[data-position="0"]') as HTMLElement;
    if (space0) {
      space0.classList.add('dramatic-glow');
    }

    // Set AR keepVisible flag during this dramatic moment
    if (this.arService) {
      this.arService.keepVisible = true;
    }
  }

  /**
   * Remove all visual effect classes from board spaces (dramatic glow + winning-space glow).
   * Called on reset and before win celebration so state is always clean.
   */
  private removeCriticalPrizeGlow(): void {
    // Search in container or fall back to document
    const boardSpaces = this.container?.querySelector('.board-spaces') || document.querySelector('.board-spaces');
    if (!boardSpaces) return;

    // Remove dramatic glow from critical prize spaces (positions 0 and 19)
    const space19 = boardSpaces.querySelector('[data-position="19"]');
    if (space19) {
      space19.classList.remove('dramatic-glow');
    }

    const space0 = boardSpaces.querySelector('[data-position="0"]');
    if (space0) {
      space0.classList.remove('dramatic-glow');
    }

    // Remove winning-space highlight from whichever space received it
    const winningEl = boardSpaces.querySelector('.winning-space');
    if (winningEl) {
      winningEl.classList.remove('winning-space');
    }

    // Release AR keepVisible flag
    if (this.arService) {
      this.arService.keepVisible = false;
    }
  }

  /**
   * Generate CSS for space positions
   */
  private generateSpacePositions(): string {
    // Get board size preset
    const boardSize = this.config?.settings.boardSize || 'medium';

    // Calculate dimensions from logical units (NO FRACTIONS!)
    const pixelsPerUnit = PIXELS_PER_UNIT[boardSize];
    const boardPixels = BOARD_SIZE_PIXELS[boardSize];
    const cornerSize = CORNER_UNITS * pixelsPerUnit;        // e.g., 4 × 20 = 80px
    const spaceWidth = SPACE_WIDTH_UNITS * pixelsPerUnit;   // e.g., 3 × 20 = 60px
    const spaceHeight = SPACE_HEIGHT_UNITS * pixelsPerUnit; // e.g., 4 × 20 = 80px
    const edgePos = boardPixels - cornerSize; // Position at opposite edge

    // Transform origin for rotations (center of prize space)
    const transformOrigin = `${spaceWidth / 2}px ${spaceHeight / 2}px`;

    // Corner positions (no rotation needed for corners, label handles it)
    const corners = [
      { pos: 0, top: `${edgePos}px`, left: `${edgePos}px` },      // Bottom-right (START)
      { pos: 6, top: `${edgePos}px`, left: '0' },                 // Bottom-left (ROLL AGAIN)
      { pos: 12, top: '0', left: '0' },                           // Top-left (ROLL AGAIN)
      { pos: 18, top: '0', left: `${edgePos}px` },                // Top-right (ROLL AGAIN)
    ];

    // CLOCKWISE FLOW (like Monopoly):
    // Side 1 (bottom side, going LEFT from START) - horizontal spaces (3w × 4h)
    const side1 = Array.from({ length: SPACES_PER_SIDE }, (_, i) => ({
      pos: i + 1,
      top: `${edgePos}px`,
      left: `${edgePos - spaceWidth - (i * spaceWidth)}px`
    }));

    // Side 2 (left side, going UP) - vertical spaces (4w × 3h)
    const side2 = Array.from({ length: SPACES_PER_SIDE }, (_, i) => ({
      pos: i + 7,
      top: `${edgePos - spaceWidth - (i * spaceWidth)}px`,  // Use spaceWidth for vertical track
      left: '0'
    }));

    // Side 3 (top side, going RIGHT) - horizontal spaces (3w × 4h)
    const side3 = Array.from({ length: SPACES_PER_SIDE }, (_, i) => ({
      pos: i + 13,
      top: '0',
      left: `${cornerSize + (i * spaceWidth)}px`
    }));

    // Side 4 (right side, going DOWN) - vertical spaces (4w × 3h)
    const side4 = Array.from({ length: SPACES_PER_SIDE }, (_, i) => ({
      pos: i + 19,
      top: `${cornerSize + (i * spaceWidth)}px`,  // Use spaceWidth for vertical track
      left: `${edgePos}px`
    }));

    const allPositions = [...corners, ...side1, ...side2, ...side3, ...side4];

    return allPositions.map(({ pos, top, left }) => `
      .space-${pos} {
        top: ${top};
        left: ${left};
      }
    `).join('');
  }

  /**
   * Initialize game state
   */
  private initializeGameState(winningPrize: any, winningIndex: number): GameBoardState {
    // Find winning position(s)
    const winningSpaces = this.boardSpaces.filter(s => s.prizeIndex === winningIndex);

    // Determine if extended play
    const isBottom4Prize = winningIndex <= 3; // Prizes 0-3 appear on Side 4
    const extendedPlayProb = this.config?.settings.extendedPlayProbability ?? 0.5;
    const isExtendedPlay = isBottom4Prize && Math.random() < extendedPlayProb;

    // Winning position
    let winningPosition: number;
    if (isExtendedPlay) {
      // Extended play - must land on Side 4 position
      winningPosition = winningSpaces.find(s => s.side === 4)?.position ?? winningSpaces[0].position;
    } else {
      // Normal play - random space with this prize
      winningPosition = winningSpaces[Math.floor(Math.random() * winningSpaces.length)].position;
    }

    return {
      currentPosition: 0,
      rollCount: 0,
      cornerCount: 0,
      winningPrizeId: winningPrize.id ?? '',
      winningPrizeIndex: winningIndex,
      winningPosition,
      isExtendedPlay,
      isRolling: false,
      isMoving: false,
      isComplete: false,
      hasWon: false
    };
  }

  /**
   * Handle single roll (called from external CTA button via play())
   *
   * This performs ONE roll cycle:
   * 1. Call onRoll callback (button disabled by game engine)
   * 2. Calculate outcome
   * 3. Roll die
   * 4. Move pawn
   * 5. Call onLand callback (button re-enabled by staying visible)
   * 6. If won, call onWin callback (button hidden by game engine)
   */
  private async handleSingleRoll(): Promise<void> {
    if (!this.gameState || this.gameState.isRolling || this.gameState.isMoving) {
      return;
    }

    // Call onRoll callback (game engine will keep button visible)
    this.onRoll?.();

    // Calculate roll outcome
    const outcome = this.calculateRollOutcome();

    // Roll die
    await this.rollDie(outcome.diceValue);

    // Move game piece
    await this.movePiece(outcome.targetPosition);

    // Check if won
    if (outcome.isWinningRoll) {
      this.gameState!.hasWon = true;
      this.handleWin();
    }
    // Note: onLand callback already called in movePiece()
    // Note: onWin callback already called in handleWin()
    // Button automatically re-enabled by game engine (showCTA stays true)
  }

  /**
   * Calculate roll outcome based on game state
   */
  private calculateRollOutcome(): RollOutcome {
    if (!this.gameState) {
      throw new Error('Game state not initialized');
    }

    const currentPos = this.gameState.currentPosition;
    const winningPos = this.gameState.winningPosition;

    // Calculate distance to winning position
    let distance = winningPos - currentPos;
    if (distance < 0) distance += 24; // Wrap around

    // Edge case: if already at winning position (e.g., both at 0)
    // This can happen if START corner is the winning space
    if (distance === 0) {
      distance = 24; // Full lap around the board
    }

    // Check if can win this roll
    if (distance <= 6) {
      // Winning roll!
      return {
        diceValue: distance,
        targetPosition: winningPos,
        isWinningRoll: true
      };
    }

    // Not winning yet - roll 6 to reach next corner
    return {
      diceValue: 6,
      targetPosition: (currentPos + 6) % 24,
      isWinningRoll: false
    };
  }

  /**
   * Roll die with animation
   */
  private async rollDie(value: number): Promise<void> {
    this.gameState!.isRolling = true;

    // Trigger 3D die roll animation via scene
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const rollDieFn = scene?.userData?.['rollDie'];
      if (rollDieFn) {
        await rollDieFn(value);
      } else {
        // Fallback if scene not ready
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } else {
      // Fallback if no canvas store
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    this.gameState!.isRolling = false;
    this.gameState!.rollCount++;
  }

  /**
   * Move game piece with animation
   */
  private async movePiece(targetPosition: number): Promise<void> {
    this.gameState!.isMoving = true;

    // Trigger 3D pawn movement animation via scene
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const movePawnFn = scene?.userData?.['movePawn'];
      if (movePawnFn) {
        await movePawnFn(targetPosition);
      } else {
        // Fallback if scene not ready
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    } else {
      // Fallback if no canvas store
      await new Promise(resolve => setTimeout(resolve, 1500));
    }

    this.gameState!.currentPosition = targetPosition;
    this.gameState!.isMoving = false;

    // Check if landed on a corner (ROLL AGAIN spaces)
    const cornerPositions = [6, 12, 18]; // 3 ROLL AGAIN corners
    if (cornerPositions.includes(targetPosition)) {
      this.gameState!.cornerCount++;

      // Play drama sound on 3rd corner
      if (this.gameState!.cornerCount === 3) {
        this.playSound(ASSET_PATHS.DRAMA_SOUND);
        this.highlightCriticalPrizes();
      }
    } else {
      // Remove glow if not on a corner or moving away from position 18
      this.removeCriticalPrizeGlow();
    }

    this.onLand?.(targetPosition);
  }

  /**
   * Handle win condition
   */
  private handleWin(): void {
    this.gameState!.isComplete = true;

    // Remove any critical prize glow effects before showing winning space
    this.removeCriticalPrizeGlow();

    // Highlight winning space with glow effect
    const boardSpaces = this.container?.querySelector('.board-spaces') || document.querySelector('.board-spaces');
    const winningSpace = boardSpaces?.querySelector(`[data-position="${this.gameState!.winningPosition}"]`) as HTMLElement;
    if (winningSpace) {
      winningSpace.classList.add('winning-space');
    }

    // Trigger confetti celebration
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const confettiService = scene?.userData?.['confettiService'];
      if (confettiService) {
        confettiService.win();
      }
    }

    // Play win sound
    this.playSound(ASSET_PATHS.WIN_SOUND);

    // Trigger onWin callback
    this.onWin?.(this.gameState!.winningPrizeId);

    // Force scene visibility during celebration (prevent AR tracking loss from hiding win)
    if (this.arService) {
      this.arService.keepVisible = true;
    }

    // Set prizeRevealed flag after celebration delay to enable navigation
    // This follows the same event-driven pattern as wheel and scratchcard
    setTimeout(() => {
      if (this.canvasStore) {
        const scene = this.canvasStore.scene;
        const appStore = scene?.userData?.['appStore'];
        if (appStore) {
          const session = appStore.session();
          appStore.setSession({
            ...session,
            prizeRevealed: true
          });
        }
      }

      // Disable keepVisible after celebration completes
      if (this.arService) {
        this.arService.keepVisible = false;
      }
    }, WIN_CELEBRATION_DELAY_MS); // 2 second delay for celebration visibility
  }

  /**
   * Play sound effect
   */
  private playSound(src: string): void {
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const audioService = scene?.userData?.['audioService'];
      if (audioService) {
        audioService.play(src, { volume: 0.7 });
      }
    }
  }
}
